import React from "react";
import { Portal } from "react-portal";
import styled, { css } from "styled-components";
import { Backdrop } from "./Backdrop";
import { ClickAway } from "./ClickAway";
import { Icon } from "./Icon";

import { mediaQueries, style } from "./style";

const BUTTON_ICON_WIDTH = "1rem";

function getTranslateValues(alignment: MobileAlignmentTypes | DesktopAlignmentTypes) {
  if (alignment === "center") {
    return "50%";
  } else if (alignment === "right") {
    return "100%";
  } else if (alignment === "fixed-bottom") {
    return "0";
  } else if (alignment === "fixed-stretch") {
    return "0";
  }
  return "0";
}

export type DesktopAlignmentTypes = "left" | "right" | "center";
type MobileAlignmentTypes = "left" | "right" | "center" | "fixed-bottom" | "fixed-stretch";

type DesktopClickAwayProps = {
  renderInPortal: boolean;
  desktopAlignment: DesktopAlignmentTypes;
  mobileAlignment: MobileAlignmentTypes;
};

const DesktopClickAway = styled(ClickAway)<DesktopClickAwayProps>`
  position: absolute;
  z-index: ${style.vars.zIndex.raised};
  width: 100%;
  ${({ renderInPortal }) =>
    renderInPortal &&
    css`
      top: 0;
      left: 0;
    `}

  ${({ mobileAlignment, desktopAlignment }) => {
    const mobileX = getTranslateValues(mobileAlignment);
    const desktopX = getTranslateValues(desktopAlignment);

    return css`
      transform: translateX(${mobileX});

      ${mediaQueries.mediumMin} {
        transform: translateX(${desktopX});
      }
    `;
  }}
`;

const StyledBackdrop = styled(Backdrop)`
  z-index: ${style.vars.zIndex.override};

  ${mediaQueries.mediumMin} {
    display: none;
  }
`;

type DropdownFromBottomProps = {
  mobileAlignment: string;
};

const DropdownFromBottom = styled.div<DropdownFromBottomProps>`
  align-self: flex-end;
  width: 100%;

  ${(props) =>
    props.mobileAlignment === "fixed-bottom" &&
    css`
      max-height: calc(50% - ${style.vars.space["8"]});
    `}

  ${(props) =>
    props.mobileAlignment === "fixed-stretch" &&
    css`
      height: 100vh;
    `}

  overflow: auto;
  -webkit-overflow-scrolling: touch;
  font-weight: ${style.vars.fontWeight.bold};
  text-align: left;
  pointer-events: auto;
  background: ${style.colors.white};
  border: ${style.vars.borderWidth.thin} solid ${style.colors.shadesOfGrey["4"]};
  border-top-left-radius: ${style.vars.borderRadius.base};
  border-top-right-radius: ${style.vars.borderRadius.base};

  /* 
    Safari hack to add padding to fixed menus. The lower 10% of the screen is reserved for the Safari
    bottom/bookmarks bar, causing the last items in these menus to become unclickable. 
  */

  @supports (-webkit-touch-callout: none) {
    padding-bottom: 114px;
  }

  .dropdown-wrap {
    border: none;
  }
`;

const DropdownTitle = styled.span`
  text-align: center;
  width: calc(100% - ${style.vars.space["4"]} - ${BUTTON_ICON_WIDTH});
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  vertical-align: middle;
  font-size: ${style.vars.fontSize.large};
  padding: 0.5rem ${style.vars.space["4"]};
  position: relative;
  top: 1px;
`;

const DropdownCloseIcon = styled(Icon)`
  width: ${BUTTON_ICON_WIDTH};
  height: ${BUTTON_ICON_WIDTH};
  color: ${style.colors.grey.primary};
`;

const DropdownCloseIconWrapper = styled.span`
  border: 0.75rem solid transparent;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &:hover ${DropdownCloseIcon} {
    color: ${style.colors.grey.darkest};
  }
`;

type DropdownHandlerProps = {
  inProp: boolean;
  clickAway?: React.MouseEventHandler<HTMLElement>;
  onCancel?: React.MouseEventHandler<HTMLElement>;
  children: any;
  renderInPortal?: boolean;
  mobileTitle?: string;
  closeIconAlignment?: string;
  closeIcon?: string;
  in?: boolean;
  timeout?: number;
  classNames?: string;
  unmountOnExit?: boolean;
  desktopAlignment?: DesktopAlignmentTypes;
  mobileAlignment?: MobileAlignmentTypes;
};

const noop = () => null;

export const DropdownHandler: React.FC<DropdownHandlerProps> = ({
  inProp,
  desktopAlignment = "left",
  mobileAlignment = "left",
  mobileTitle,
  clickAway,
  renderInPortal = false,
  closeIconAlignment = "right",
  closeIcon = "close",
  children,
  onCancel,
}) => {
  const isMobile = mediaQueries.smallMax.useMatchesBreakpoint();
  const isMobileFixedTransition = (mobileAlignment === "fixed-bottom" || mobileAlignment === "fixed-stretch") && isMobile;

  if (!inProp) {
    return null;
  }

  let dropdown = (
    <DesktopClickAway
      onClick={clickAway ?? noop}
      renderInPortal={renderInPortal}
      desktopAlignment={desktopAlignment}
      mobileAlignment={mobileAlignment}
    >
      {children}
    </DesktopClickAway>
  );

  if (isMobileFixedTransition) {
    dropdown = (
      <StyledBackdrop>
        <ClickAway mobile onClick={clickAway ?? noop}>
          <DropdownFromBottom mobileAlignment={mobileAlignment}>
            {closeIconAlignment === "left" && (
              <DropdownCloseIconWrapper onClick={onCancel ?? clickAway ?? noop}>
                <DropdownCloseIcon icon={closeIcon} />
              </DropdownCloseIconWrapper>
            )}
            <DropdownTitle>{mobileTitle}</DropdownTitle>
            {closeIconAlignment === "right" && (
              <DropdownCloseIconWrapper onClick={onCancel ?? clickAway ?? noop}>
                <DropdownCloseIcon icon={closeIcon} />
              </DropdownCloseIconWrapper>
            )}
            {children}
          </DropdownFromBottom>
        </ClickAway>
      </StyledBackdrop>
    );
  }

  if (isMobileFixedTransition || renderInPortal) {
    return <Portal>{dropdown}</Portal>;
  }

  return dropdown;
};
